import { DEFAULT_SYMBOLS } from '@/config/constants'
import type { OnboardingConfigInput } from './config-schema'

/**
 * Bundled fallback onboarding config (version 1).
 *
 * The authoritative copy will live admin-managed in edge-admin-api (Phase 3);
 * this snapshot keeps first-run working offline/dev and when the server config
 * is unavailable or invalid. Question wording approved 2026-08-22 (spec D1);
 * provisioning table per spec D3.
 *
 * Scoring notes:
 * - Q4 (tooling) is analytics-only: no scores by design. It is required, with a
 *   catch-all "Something else" option, since the survey cannot be skipped.
 * - `goal: strategies` outweighs style answers so "test and build strategies"
 *   lands strategist regardless of style (spec D2).
 * - Ties resolve by `personas` array order (see persona.ts).
 */
export const DEFAULT_ONBOARDING_CONFIG: OnboardingConfigInput = {
  version: 1,
  personas: ['newcomer', 'day-trader', 'swing-trader', 'researcher', 'strategist'],
  questions: [
    {
      id: 'experience',
      prompt: 'Have you traded before?',
      description: 'This helps us set up the right starting point for you.',
      optional: false,
      skipIf: null,
      options: [
        { id: 'never', label: 'Not yet', scores: { newcomer: 3 } },
        { id: 'lt1y', label: 'Getting started (under a year)', scores: { newcomer: 1 } },
        { id: '1to5y', label: '1 to 5 years', scores: {} },
        { id: 'veteran', label: 'Veteran', scores: {} },
      ],
    },
    {
      id: 'style',
      prompt: 'How do you like to trade?',
      optional: false,
      skipIf: { questionId: 'experience', optionId: 'never' },
      options: [
        { id: 'day', label: 'Day trading', scores: { 'day-trader': 2 } },
        { id: 'swing', label: 'Swing trading', scores: { 'swing-trader': 2 } },
        { id: 'longterm', label: 'Long-term investing', scores: { researcher: 2 } },
        { id: 'options', label: 'Options', scores: { 'day-trader': 1, 'swing-trader': 1 } },
      ],
    },
    {
      id: 'goal',
      prompt: 'What brings you to Edge?',
      optional: false,
      skipIf: null,
      options: [
        {
          id: 'find-stocks',
          label: 'Find stocks to trade',
          scores: { 'day-trader': 1, 'swing-trader': 1 },
        },
        { id: 'research-tickers', label: 'Research specific tickers', scores: { researcher: 2 } },
        { id: 'strategies', label: 'Test and build strategies', scores: { strategist: 3 } },
        { id: 'learn', label: 'Learn how to trade', scores: { newcomer: 2 } },
      ],
    },
    {
      id: 'tooling',
      prompt: 'What are you using today?',
      description: 'This helps us understand where you are coming from.',
      optional: false,
      skipIf: null,
      options: [
        { id: 'scanner-product', label: 'Trade-Ideas or another scanner', scores: {} },
        { id: 'broker', label: 'A broker platform', scores: {} },
        { id: 'spreadsheets', label: 'Spreadsheets and news', scores: {} },
        { id: 'nothing', label: 'Nothing yet', scores: {} },
        { id: 'other', label: 'Something else', scores: {} },
      ],
    },
  ],
  provisioning: {
    // Newcomers get the Day Trader workspace (Chris, 2026-09-20): the same
    // layout a day trader lands in, with the beginner track and checklist.
    newcomer: {
      presetId: 'preset_day_trader',
      trackId: 'trading-basics',
      checklistId: 'beginner',
      landingRoute: null,
      seed: { watchlist: [...DEFAULT_SYMBOLS], chartTicker: 'SPY' },
    },
    'day-trader': {
      presetId: 'preset_day_trader',
      trackId: 'master-scanner',
      checklistId: 'scanner',
      landingRoute: null,
      seed: { watchlist: [...DEFAULT_SYMBOLS], chartTicker: null },
    },
    'swing-trader': {
      presetId: 'preset_swing_trader',
      trackId: 'master-scanner',
      checklistId: 'scanner',
      landingRoute: null,
      seed: { watchlist: [...DEFAULT_SYMBOLS], chartTicker: null },
    },
    researcher: {
      presetId: 'preset_research',
      trackId: null,
      checklistId: 'research',
      landingRoute: null,
      seed: { watchlist: [...DEFAULT_SYMBOLS], chartTicker: null },
    },
    // Every persona lands on the dashboard (Chris, 2026-09-19), strategists
    // included: the provisioned workspace and the Getting Started card both
    // live there. `landingRoute` stays null across the board.
    strategist: {
      presetId: 'preset_research',
      trackId: null,
      checklistId: 'strategist',
      landingRoute: null,
      seed: { watchlist: [...DEFAULT_SYMBOLS], chartTicker: null },
    },
  },
  // Item auto-completion: `walkthrough` items complete when that walkthrough
  // finishes; `signal` items complete when the app emits the signal `target`
  // times (checklist-signals.ts). Items with neither are manual-tick only.
  checklists: {
    beginner: {
      items: [
        { id: 'first-chart', label: 'Open a chart and look around', signal: 'chart-open' },
        {
          id: 'first-scan',
          label: 'Run your first scan',
          walkthrough: 'scanner.edit-filters',
          panel: 'scanner',
        },
        {
          id: 'watchlist-3',
          label: 'Add 3 stocks to your watchlist',
          signal: 'watchlist-add',
          target: 3,
        },
        { id: 'challenge', label: 'Try a chart challenge', signal: 'challenge-complete' },
        { id: 'ask-companion', label: 'Ask the AI companion a question', signal: 'companion-ask' },
      ],
    },
    scanner: {
      items: [
        {
          id: 'first-scan',
          label: 'Run your first scan',
          walkthrough: 'scanner.edit-filters',
          panel: 'scanner',
        },
        {
          id: 'add-columns',
          label: 'Add the columns you care about',
          walkthrough: 'scanner.edit-columns',
          panel: 'scanner',
        },
        {
          id: 'color-code',
          label: 'Color-code a column',
          walkthrough: 'columns-dashboard.color-code-column',
          panel: 'scanner',
        },
        {
          id: 'watchlist-3',
          label: 'Add 3 stocks to your watchlist',
          signal: 'watchlist-add',
          target: 3,
        },
        { id: 'ask-companion', label: 'Ask the AI companion a question', signal: 'companion-ask' },
      ],
    },
    research: {
      items: [
        {
          id: 'first-ticker',
          label: 'Open a chart on a ticker you follow',
          signal: 'chart-open',
        },
        {
          id: 'watchlist-3',
          label: 'Add 3 stocks to your watchlist',
          signal: 'watchlist-add',
          target: 3,
        },
        {
          id: 'first-scan',
          label: 'Run a scan to find new names',
          walkthrough: 'scanner.edit-filters',
          panel: 'scanner',
        },
        { id: 'ask-companion', label: 'Ask the AI companion a question', signal: 'companion-ask' },
      ],
    },
    // No detection points until the backtest-home pre-built catalog ships;
    // these stay manual-tick items on the card.
    strategist: {
      items: [
        { id: 'run-prebuilt', label: 'Run a pre-built strategy', walkthrough: null },
        { id: 'preview-results', label: 'Preview its results', walkthrough: null },
        { id: 'tweak-rerun', label: 'Tweak one filter and re-run', walkthrough: null },
        { id: 'save-copy', label: 'Save your copy', walkthrough: null },
      ],
    },
  },
  tips: {
    scanner: {
      walkthrough: 'scanner.edit-filters',
      panel: 'scanner',
      label: 'New to the Scanner? Take a 60-second tour.',
    },
  },
}
